import React from "react";
import { motion } from "framer-motion";
import {
  FaBolt,
  FaChartLine,
  FaGem,
  FaGlobe,
  FaLayerGroup,
  FaLock,
  FaShieldAlt,
  FaUsers,
} from "react-icons/fa";
import { BRAND } from "../config/brand";
import CosmicPyramidCanvas from "./CosmicPyramidCanvas";

const fadeUp = {
  initial: { opacity: 0, y: 36 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.75, ease: "easeOut" },
  viewport: { once: true, amount: 0.22 },
};

const coreBlocks = [
  {
    title: "Asset backed reserve design",
    text: "The ecosystem is built around a simple holder promise: digital access paired with a real reserve narrative, clear token data, and public-facing transparency.",
  },
  {
    title: "Exchange-ready token flow",
    text: "The presale journey is prepared for stable purchase assets, wallet onboarding, liquidity planning, and a cleaner route from early participation to market access.",
  },
  {
    title: "Proof of value discipline",
    text: "Every section pushes the same message: the token should feel measurable, auditable, and easier to understand than a purely speculative asset.",
  },
];

const partnerTracks = [
  "Vault custody",
  "Audit readiness",
  "Liquidity partners",
  "Exchange pipeline",
  "Market education",
  "Community growth",
];

const whyCards = [
  {
    icon: FaShieldAlt,
    title: "Secure",
    text: "Built around wallet-first access, reserve visibility, and careful presale controls.",
  },
  {
    icon: FaLayerGroup,
    title: "Scalable",
    text: "Binance Smart Chain compatibility keeps the experience familiar for presale buyers and DeFi users.",
  },
  {
    icon: FaLock,
    title: "Transparent",
    text: "Token supply, payment assets, contract details, and reserve messaging stay visible across the page.",
  },
];

const whereItems = [
  {
    label: "Presale live",
    value: "USDT / USDC",
  },
  {
    label: "Network",
    value: BRAND.chainName,
  },
  {
    label: "Token price",
    value: BRAND.tokenPriceLabel,
  },
  {
    label: "Next market step",
    value: "DEX liquidity",
  },
];

const recentItems = [
  {
    title: "High-impact identity",
    text: "The new brand system gives the presale a distinct visual language across hero, sections, and footer.",
  },
  {
    title: "Expansion-ready content",
    text: "The brand config and reusable content structure make future ecosystem updates easier to publish across the site.",
  },
  {
    title: "Presale flow refined",
    text: "Wallet connect, stable payment options, token receive display, and add-to-wallet flow are already part of the purchase card.",
  },
];

function PresaleStorySections() {
  return (
    <div className="presale-story">
      {/* <CosmicPyramidCanvas
        focusX={0.400}
        focusY={0.34}
        isVisiblePramid={true} /> */}
      <motion.section className="story-section story-foundation" {...fadeUp}>
        <div className="story-copy">
          <span>POWERING ASSET-BACKED WEB3</span>
          <h2>Blockchain L1 Thinking for a Cleaner Presale Economy</h2>
          <h6>
            {BRAND.name} takes the strongest ideas from modern presale pages:
            clear asset positioning, transparent token access, practical wallet
            flow, and a roadmap that points toward real liquidity instead of
            short-term noise.
          </h6>
        </div>
        <div className="story-feature-list">
          {coreBlocks.map((item) => (
            <article className="right" key={item.title}>
              <FaBolt />
              <div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </div>
            </article>
          ))}
        </div>
      </motion.section>

      <motion.section className="story-section story-proof" id="proof-of-value" {...fadeUp}>
        <div className="story-dark-panel">
          <span>CONSENSUS THROUGH TRANSPARENCY</span>
          <h2>Proof of Economy</h2>
          <p>
            A presale needs more than a token price. It needs an economy that
            buyers can read quickly: what backs the story, how supply is shown,
            where liquidity is expected, and why the project can continue after
            the first purchase.
          </p>
        </div>
      </motion.section>

      <motion.section className="story-section story-why" id="why-1eye" {...fadeUp}>
        <div className="story-heading">
          <span>WHY {BRAND.name}</span>
          <h2>Designed for Trust, Access, and Market Readiness</h2>
          <p>
            Every section is aligned around one buyer message:
            {` ${BRAND.tokenSymbol} `}is positioned as a premium,
            asset backed token with a simple purchase path.
          </p>
        </div>
        <div className="why-grid">
          {whyCards.map(({ icon: Icon, title, text }) => (
            <article key={title}>
              <Icon />
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </motion.section>

      <motion.section className="story-section story-where" id="where-1eye" {...fadeUp}>
        <div className="story-copy">
          <span>WHERE TO ACCESS</span>
          <h2>From Presale Entry to Liquidity Launch</h2>
          <p>
            Buyers can enter with stable assets during presale, receive
            {` ${BRAND.tokenSymbol} `}through the connected wallet flow, and
            follow the project toward liquidity expansion after the initial
            allocation phase.
          </p>
        </div>
        <div className="where-grid">
          {whereItems.map((item) => (
            <article key={item.label}>
              <small>{item.label}</small>
              <strong>{item.value}</strong>
            </article>
          ))}
        </div>
      </motion.section>

      <motion.section className="story-section story-partners" id="partners" {...fadeUp}>
        <div className="story-heading">
          <span>OUR PARTNERS</span>
          <h2>Partner Tracks That Matter for a Presale Page</h2>
        </div>
        <div className="partner-strip" aria-label="Partner categories">
          {partnerTracks.map((track) => (
            <div key={track}>
              <FaGlobe />
              <span>{track}</span>
            </div>
          ))}
        </div>
      </motion.section>

      <motion.section className="story-section story-recent" id="recent" {...fadeUp}>
        <div className="story-heading">
          <span>RECENT ACHIEVEMENTS</span>
          <h2>What Has Already Been Strengthened</h2>
        </div>
        <div className="recent-stack">
          {recentItems.map((item, index) => {
            const icons = [FaGem, FaUsers, FaChartLine];
            const Icon = icons[index];
            return (
              <article key={item.title}>
                <Icon />
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>
              </article>
            );
          })}
        </div>
      </motion.section>
    </div>
  );
}

export default PresaleStorySections;
